import React, { createContext, useContext } from 'react';

/**
 * React Context that provides the current chart context group identity.
 *
 * Each BeaconChartWithLegend section can provide a contextGroup string
 * so that all chart components rendered inside automatically register
 * their Highcharts options under that group. This enables scoped
 * payloads -- when "Ask Advisor" is triggered from a section, only
 * charts in that group are included.
 *
 * @see ARCHITECTURE.md for full design documentation.
 */
const ChartContextGroupContext = createContext<string | undefined>(undefined);

export const ChartContextGroupProvider = ChartContextGroupContext.Provider;

/**
 * Read the current chart context group from React Context.
 * Returns undefined if not inside a ChartContextGroupProvider.
 */
export const useChartContextGroup = (): string | undefined => useContext(ChartContextGroupContext);

// ---------------------------------------------------------------------------
// Chart Name Context
// ---------------------------------------------------------------------------

/**
 * React Context that provides the chart name for the current section.
 *
 * For 1:1 group-to-chart sections (one contextGroup wraps exactly one chart),
 * the layout can set chartName via ChartContextProvider so the chart primitive
 * picks it up automatically -- no prop drilling needed.
 *
 * For multi-chart groups (e.g. TRAFFIC with donut + bars), leave chartName
 * undefined and let each chart primitive pass its own chartContextId prop.
 */
const ChartContextNameContext = createContext<string | undefined>(undefined);

/**
 * Read the current chart name from React Context.
 * Returns undefined if not inside a provider that sets chartName.
 */
export const useChartContextName = (): string | undefined => useContext(ChartContextNameContext);

// ---------------------------------------------------------------------------
// Compound Provider
// ---------------------------------------------------------------------------

/**
 * Compound provider that sets both contextGroup and chartName in one wrapper.
 *
 * Usage in layouts:
 * ```tsx
 * // 1:1 section (one group, one chart):
 * <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_TRAFFIC} chartName={CHART_NAMES.ORGANIC_V2_TRAFFIC_SPLINE}>
 *   <AtlasV2OrganicTrafficV2OrganicTrafficSplineChart />
 * </ChartContextProvider>
 *
 * // Multi-chart section or shared component with hardcoded chartName:
 * <ChartContextProvider group={CHART_CONTEXT_GROUPS.ORGANIC_V2_TOP_ENTITY_KEYWORD}>
 *   <AtlasV2TrafficDropdownTopEntityTraffic ... />
 * </ChartContextProvider>
 * ```
 */
export const ChartContextProvider: React.FC<{
  group: string;
  chartName?: string;
  children: React.ReactNode;
}> = ({ group, chartName, children }) => (
  <ChartContextGroupProvider value={group}>
    <ChartContextNameContext.Provider value={chartName}>{children}</ChartContextNameContext.Provider>
  </ChartContextGroupProvider>
);
